import { useEffect, useState, useContext } from 'react';
import Layout from '@/components/Layout';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Brain, ArrowLeft, Trash2 } from 'lucide-react';
import { AuthContext } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_LABELS = {
  'basic': 'BASIC PRACTICE',
  'comps_ready': "COMP'S READY",
  'advanced': 'ADVANCED'
};

export default function MemoryGameCategoryPage() {
  const { tier, categorySlug } = useParams();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';
  const [games, setGames] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, [tier, categorySlug]);

  const loadData = async () => {
    try {
      // Load all memory games and filter by tier and category
      const [gamesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/memory-games`),
        axios.get(`${API}/test-categories`) // Public endpoint
      ]);
      
      // Find the category
      const foundCategory = categoriesRes.data.find(cat => cat.slug === categorySlug);
      setCategory(foundCategory);
      
      // Filter games for this tier and category
      const filtered = gamesRes.data.filter(
        g => g.tier === tier && g.category === categorySlug
      );
      setGames(filtered);
    } catch (error) {
      console.error('Failed to load memory games:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGameClick = (gameId) => {
    navigate(`/memory-game/${gameId}`);
  };

  const handleBackClick = () => {
    navigate('/memory-game-center');
  };

  const handleDelete = async (gameId, title) => {
    if (!window.confirm(`Delete Memory game "${title || 'this game'}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/memory-games/${gameId}`, { withCredentials: true });
      setGames(prev => prev.filter(g => g.id !== gameId));
      toast.success('Memory game deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete game');
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <div 
        className="relative bg-gradient-to-br from-pink-400 via-red-400 to-pink-500 h-[300px] mb-12 flex items-center justify-center"
      >
        <div className="container mx-auto px-6">
          <button
            onClick={handleBackClick}
            className="flex items-center gap-2 bg-[#2C1810]/90 text-[#F4E4C1] font-bold px-4 py-2 rounded-lg hover:bg-[#2C1810] transition-all mb-6"
          >
            <ArrowLeft className="w-6 h-6" />
            <span className="text-lg">Back to Memory Game Center</span>
          </button>
          <div className="text-center">
            <div className="text-6xl mb-4">{category?.icon || '🧠'}</div>
            <h1 className="text-5xl md:text-6xl retro-title text-white mb-2">
              {category?.name.toUpperCase() || categorySlug.toUpperCase()}
            </h1>
            <p className="text-xl text-white font-bold mb-2">
              {TIER_LABELS[tier] || tier.toUpperCase()}
            </p>
            <p className="text-lg text-white opacity-90">
              {games.length} Memory Game{games.length !== 1 ? 's' : ''} Available
            </p>
          </div>
        </div>
      </div>

      {/* Games Grid */}
      <div className="container mx-auto px-6 py-12">
        {loading ? (
          <div className="text-center py-20">
            <div className="text-2xl text-[#6B4423]">Loading games...</div>
          </div>
        ) : games.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {games.map((game) => (
              <div key={game.id} className="relative" data-testid={`memory-game-card-${game.id}`}>
                {isAdmin && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(game.id, game.test_title); }}
                    className="absolute top-3 right-3 z-10 bg-[#E85D4D] text-white p-2 rounded-full hover:bg-[#d14a3b] transition-colors shadow-lg"
                    title="Delete this Memory game"
                    data-testid={`delete-memory-game-button-${game.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => handleGameClick(game.id)}
                  className="w-full bg-gradient-to-br from-pink-100 to-red-100 border-4 border-[#FF6B9D] p-8 rounded-lg text-left transition-all transform hover:scale-105 hover:shadow-2xl"
                >
                  <div className="flex items-start justify-between mb-4">
                    <Brain className="w-10 h-10 text-[#C44569]" />
                    <span className="bg-[#FF6B9D] text-white px-3 py-1 rounded-full text-sm font-bold">
                      {game.sets && game.sets.length > 0 ? game.sets.flat().length : (game.cards?.length || 0)} Pairs
                      {game.sets && game.sets.length > 1 ? ` (${game.sets.length} sets)` : ''}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#6B4423] mb-3 retro-title">
                    {game.test_title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Match term-definition pairs. Test your memory!
                  </p>
                  <div className="text-xs text-gray-500">
                    Created {new Date(game.created_at).toLocaleDateString()}
                  </div>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Brain className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <div className="text-2xl text-gray-600 mb-2">No memory games in this category yet</div>
            <p className="text-gray-500">Check back later or try another category!</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
